import { create } from "zustand";
import { todayISO } from "@/engine/date";
import { createEmptyProject, createSampleProject } from "@/engine/sample";
import type { OrganizationTeam, Project, UserResource } from "@/engine/types";
import type { WorkspaceMembership, WorkspaceTemplate } from "@/engine/workspace-seeds";
import { membershipFromInviteRole } from "@/features/workspace/workspace-face";
import { normalizeExecution } from "./execution-task-ops";
import { newId } from "./tree-ops";
import { reportSaveFailure, reportSaveOk } from "./save-health";
import {
  getCatalogCache,
  normalizeProjectResources,
  replaceCatalogCache,
  saveCatalogNow,
  scheduleCatalogSave,
  SEED_REVISION,
  seedCatalog,
  STORAGE_V3,
  type PersistedV3,
  type ProjectChrome,
  type ProjectTab,
  type ThemePref,
  type UserProfile,
  type WorkspaceRecord,
} from "./persist";
import { upsertWorkspaceDocTemplate, removeWorkspaceDocTemplate, type WorkspaceDocTemplateDraft } from "@/features/docs/doc-templates";
import { normalizeWeeklyNote, type UserWeeklyNote } from "@/features/user-weekly/user-weekly-model";
import { applyProjectTemplate, templateFromProject } from "@/features/retrospective/retrospective-model";
import {
  buildWorkspaceRecord,
  deleteWorkspaceRecord,
  listWorkspaceSummaries,
  patchWorkspaceMeta,
  refillWorkspace,
  type WorkspaceSummary,
} from "./workspace-ops";

export type { WorkspaceSummary, WorkspaceRecord };
export type { WorkspaceTemplate };

export interface ProjectSummary {
  id: string;
  name: string;
  start: string;
  updatedAt: string;
  /** schedule (WBS/Activity) rows */
  taskCount: number;
  /** Planner execution tasks */
  executionCount: number;
  /** execution tasks not yet done */
  openExecutionCount: number;
  lastTab: ProjectTab;
}

export interface ResourceWorkloadSummary {
  resourceId: string;
  totalAllocationRatio: number; // e.g. 1.6 = 160%
  totalPlannedMM: number;
  projectCount: number;
  isOverAllocated: boolean; // totalAllocationRatio > 1.05
  assignments: Array<{
    projectId: string;
    projectName: string;
    projectRole: string;
    allocationRatio: number;
    plannedMM: number;
    startDate: string;
    endDate: string;
  }>;
}

interface CatalogState {
  workspaces: Record<string, WorkspaceRecord>;
  activeWorkspaceId: string;
  projects: Record<string, Project>;
  byProject: WorkspaceRecord["byProject"];
  activeProjectId: string | null;
  theme: ThemePref;
  profile: UserProfile;
  myTasksAssignee: string;
  navCollapsed: boolean;
  /** 개인 주간 보고. 키는 사람 이름과 주 시작일. */
  userWeekly: Record<string, UserWeeklyNote>;
  /** 참여 목업을 한 번 넣은 뒤. 지우면 다시 만들지 않는다. */
  sampleJoinedWorkspace: boolean;
  resources: Record<string, UserResource>;
  teams: Record<string, OrganizationTeam>;
}

interface CatalogActions {
  list: () => ProjectSummary[];
  getProject: (id: string) => Project | null;
  getChrome: (id: string) => ProjectChrome;
  createProject: (opts: { name: string; start?: string; template: "empty" | "sample"; projectTemplateId?: string }) => string;
  saveProjectTemplate: (project: Project) => string | null;
  loadDemoCatalog: () => string;
  listWorkspaces: () => WorkspaceSummary[];
  switchWorkspace: (id: string) => boolean;
  createWorkspace: (opts: {
    name: string;
    icon?: string;
    color?: string;
    imageUrl?: string | null;
    description?: string;
    template: WorkspaceTemplate;
    role?: WorkspaceMembership;
  }) => string;
  joinWorkspaceFromInvite: (opts: { name: string; roleLabel: string }) => string | null;
  updateWorkspace: (id: string, patch: { name?: string; icon?: string; color?: string; description?: string; imageUrl?: string | null }) => boolean;
  saveWorkspaceDocTemplate: (draft: WorkspaceDocTemplateDraft) => boolean;
  deleteWorkspaceDocTemplate: (id: string) => boolean;
  deleteWorkspace: (id: string) => string | null;
  importProjectData: (project: Project) => string;
  deleteProject: (id: string) => void;
  saveProject: (project: Project, chrome: ProjectChrome) => void;
  mutateProject: (id: string, fn: (p: Project) => Project) => boolean;
  setActiveProjectId: (id: string | null) => void;
  setOpenPlanIds: (ids: string[]) => void;
  setTheme: (theme: ThemePref) => void;
  setProfile: (profile: Partial<UserProfile>) => void;
  setMyTasksAssignee: (name: string) => void;
  setNavCollapsed: (collapsed: boolean) => void;
  setUserWeeklyNote: (key: string, note: UserWeeklyNote) => void;
  // Resource & Team actions
  addResource: (resource: Omit<UserResource, "id">) => string;
  updateResource: (id: string, patch: Partial<UserResource>) => void;
  deleteResource: (id: string) => void;
  addTeam: (team: Omit<OrganizationTeam, "id">) => string;
  updateTeam: (id: string, patch: Partial<OrganizationTeam>) => void;
  deleteTeam: (id: string) => void;
  applyOrgSnapshot: (teams: Record<string, OrganizationTeam>, resources: Record<string, UserResource>) => void;
  getResourceWorkload: (resourceId: string) => ResourceWorkloadSummary;
  getAllWorkloads: () => Record<string, ResourceWorkloadSummary>;
}

function flushWorkspaces(s: CatalogState): Record<string, WorkspaceRecord> {
  const current = s.workspaces[s.activeWorkspaceId];
  if (!current) return s.workspaces;
  return {
    ...s.workspaces,
    [s.activeWorkspaceId]: {
      ...current,
      projects: s.projects,
      byProject: s.byProject,
      resources: s.resources,
      teams: s.teams,
      activeProjectId: s.activeProjectId,
    },
  };
}

function snapshot(s: CatalogState): PersistedV3 {
  return {
    version: 3,
    activeWorkspaceId: s.activeWorkspaceId,
    workspaces: flushWorkspaces(s),
    globalUi: {
      theme: s.theme,
      profile: s.profile,
      myTasksAssignee: s.myTasksAssignee,
      navCollapsed: s.navCollapsed,
      ...(Object.keys(s.userWeekly).length ? { userWeekly: s.userWeekly } : {}),
    },
    seedRevision: SEED_REVISION,
    sampleJoinedWorkspace: s.sampleJoinedWorkspace,
  };
}

function persist(s: CatalogState, immediate = false) {
  const data = snapshot(s);
  replaceCatalogCache(data);
  if (immediate) {
    try {
      saveCatalogNow(data);
      reportSaveOk();
    } catch (error) {
      reportSaveFailure(error);
    }
  } else scheduleCatalogSave(data, reportSaveOk, reportSaveFailure);
}

/** 지금 상태를 바로 저장한다(종료 직전 flush). 실패하면 던진다. */
export function saveCatalogImmediately(): void {
  const data = snapshot(useCatalogStore.getState());
  replaceCatalogCache(data);
  saveCatalogNow(data);
  reportSaveOk();
}

/** 저장 형식 그대로의 현재 카탈로그(활성 워크스페이스를 되돌려 넣은 값). MCP read-model이 읽는다. */
export function catalogSnapshot(): PersistedV3 {
  return snapshot(useCatalogStore.getState());
}

function applyWorkspace(s: CatalogState, workspaces: Record<string, WorkspaceRecord>, activeWorkspaceId: string): CatalogState {
  const ws = workspaces[activeWorkspaceId] ?? Object.values(workspaces)[0];
  if (!ws) {
    const seeded = seedCatalog();
    return toState(seeded);
  }
  return {
    ...s,
    workspaces,
    activeWorkspaceId: ws.id,
    projects: ws.projects,
    byProject: ws.byProject,
    resources: ws.resources,
    teams: ws.teams,
    activeProjectId: ws.activeProjectId,
  };
}

function toState(data: PersistedV3): CatalogState {
  let workspaces = data.workspaces;
  let activeWorkspaceId = data.activeWorkspaceId;
  if (!workspaces || Object.keys(workspaces).length === 0) {
    const seeded = seedCatalog();
    workspaces = seeded.workspaces;
    activeWorkspaceId = seeded.activeWorkspaceId;
  }
  if (!workspaces[activeWorkspaceId]) activeWorkspaceId = Object.keys(workspaces)[0]!;
  const ws = workspaces[activeWorkspaceId]!;
  return {
    workspaces,
    activeWorkspaceId,
    projects: ws.projects,
    byProject: ws.byProject,
    resources: ws.resources,
    teams: ws.teams,
    activeProjectId: ws.activeProjectId,
    theme: data.globalUi.theme,
    profile: data.globalUi.profile,
    myTasksAssignee: data.globalUi.myTasksAssignee ?? data.globalUi.profile.displayName,
    navCollapsed: data.globalUi.navCollapsed === true,
    userWeekly: data.globalUi.userWeekly ?? {},
    sampleJoinedWorkspace: data.sampleJoinedWorkspace === true,
  };
}

const boot = typeof localStorage === "undefined" ? seedCatalog() : getCatalogCache();
if (typeof localStorage !== "undefined" && !localStorage.getItem(STORAGE_V3)) {
  const initialized = toState(boot);
  persist(initialized, true);
}

export const useCatalogStore = create<CatalogState & CatalogActions>()((set, get) => ({
  ...toState(boot),

  list: () => {
    const { projects, byProject } = get();
    return Object.values(projects)
      .map((p) => ({
        id: p.id,
        name: p.name,
        start: p.start,
        updatedAt: p.updatedAt,
        taskCount: p.tasks.length,
        executionCount: p.executionTasks.length,
        openExecutionCount: p.executionTasks.filter((t) => t.status !== "done").length,
        lastTab: byProject[p.id]?.lastTab ?? "schedule",
      }))
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
  },

  getProject: (id) => get().projects[id] ?? null,

  getChrome: (id) => get().byProject[id] ?? { checkpoints: [], collapsed: [] },

  createProject: ({ name, start, template, projectTemplateId }) => {
    const trimmed = name.trim() || "새 플랜";
    let project: Project;
    const saved = projectTemplateId
      ? get().workspaces[get().activeWorkspaceId]?.projectTemplates?.find((item) => item.id === projectTemplateId)
      : undefined;
    if (saved) {
      project = applyProjectTemplate(createEmptyProject(trimmed, start || todayISO()), saved);
      project.id = newId("p");
      project.name = trimmed;
      project.updatedAt = new Date().toISOString();
    } else if (template === "sample") {
      project = createSampleProject();
      project.id = newId("p");
      project.name = trimmed;
      if (start) {
        project.start = start;
        if (project.statusDate < start) project.statusDate = start;
      }
      project.updatedAt = new Date().toISOString();
    } else {
      project = createEmptyProject(trimmed, start || todayISO());
      project.id = newId("p");
    }
    project = normalizeProjectResources(project);
    set((s) => {
      const next: CatalogState = {
        ...s,
        projects: { ...s.projects, [project.id]: project },
        byProject: { ...s.byProject, [project.id]: { checkpoints: [], collapsed: [] } },
        activeProjectId: project.id,
      };
      persist(next, true);
      return next;
    });
    return project.id;
  },

  saveProjectTemplate: (project) => {
    const workspaceId = get().activeWorkspaceId;
    const current = get().workspaces[workspaceId];
    if (!current || project.tasks.length === 0) return null;
    const template = templateFromProject(project, newId("tpl"), new Date().toISOString());
    set((s) => {
      const ws = s.workspaces[workspaceId];
      if (!ws) return s;
      const next: CatalogState = {
        ...s,
        workspaces: { ...s.workspaces, [workspaceId]: { ...ws, projectTemplates: [...(ws.projectTemplates ?? []), template] } },
      };
      persist(next, true);
      return next;
    });
    return template.id;
  },

  loadDemoCatalog: () => {
    let activeProjectId: string | null = null;
    set((s) => {
      const current = flushWorkspaces(s)[s.activeWorkspaceId];
      if (!current) return s;
      const refilled = refillWorkspace(current);
      const next = applyWorkspace(s, { ...s.workspaces, [refilled.id]: refilled }, refilled.id);
      persist(next, true);
      activeProjectId = next.activeProjectId;
      return next;
    });
    return activeProjectId ?? "";
  },

  listWorkspaces: () => listWorkspaceSummaries(flushWorkspaces(get())),

  switchWorkspace: (id) => {
    const s = get();
    if (!s.workspaces[id] || s.activeWorkspaceId === id) return false;
    const workspaces = flushWorkspaces(s);
    const next = applyWorkspace(s, workspaces, id);
    persist(next, true);
    set(next);
    return true;
  },

  createWorkspace: ({ name, icon, color, imageUrl, description, template, role }) => {
    const id = newId("ws");
    const record = buildWorkspaceRecord({
      id,
      name,
      icon,
      color,
      imageUrl,
      description,
      template,
      role,
      index: Object.keys(get().workspaces).length,
    });
    set((s) => {
      const workspaces = { ...flushWorkspaces(s), [id]: record };
      const next = applyWorkspace(s, workspaces, id);
      persist(next, true);
      return next;
    });
    return id;
  },

  joinWorkspaceFromInvite: ({ name, roleLabel }) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const existing = Object.values(flushWorkspaces(get())).find((ws) => ws.name === trimmed);
    if (existing) return existing.id;
    const id = newId("ws");
    const record = buildWorkspaceRecord({
      id,
      name: trimmed,
      template: "empty",
      role: membershipFromInviteRole(roleLabel),
      index: Object.keys(get().workspaces).length,
    });
    set((s) => {
      const workspaces = { ...flushWorkspaces(s), [id]: record };
      const next = applyWorkspace(s, workspaces, s.activeWorkspaceId);
      persist(next, true);
      return next;
    });
    return id;
  },

  updateWorkspace: (id, patch) => {
    const s = get();
    const workspaces = patchWorkspaceMeta(flushWorkspaces(s), id, patch);
    if (!workspaces) return false;
    const next = applyWorkspace(s, workspaces, s.activeWorkspaceId);
    persist(next, true);
    set(next);
    return true;
  },

  saveWorkspaceDocTemplate: (draft) => {
    const s = get();
    const workspaces = flushWorkspaces(s);
    const current = workspaces[s.activeWorkspaceId];
    if (!current) return false;
    const author = draft.updatedBy || s.profile?.displayName || "나";
    const docTemplates = upsertWorkspaceDocTemplate(
      current.docTemplates ?? [],
      { ...draft, updatedBy: author },
      new Date().toISOString(),
      undefined,
      author,
    );
    if (!docTemplates) return false;
    const next = applyWorkspace(s, { ...workspaces, [current.id]: { ...current, docTemplates } }, s.activeWorkspaceId);
    persist(next, true);
    set(next);
    return true;
  },

  deleteWorkspaceDocTemplate: (id) => {
    const s = get();
    const workspaces = flushWorkspaces(s);
    const current = workspaces[s.activeWorkspaceId];
    if (!current) return false;
    const docTemplates = removeWorkspaceDocTemplate(current.docTemplates ?? [], id);
    if (!docTemplates) return false;
    const next = applyWorkspace(s, { ...workspaces, [current.id]: { ...current, docTemplates } }, s.activeWorkspaceId);
    persist(next, true);
    set(next);
    return true;
  },

  deleteWorkspace: (id) => {
    const s = get();
    const removed = deleteWorkspaceRecord(flushWorkspaces(s), id);
    if (!removed) return null;
    const stay = s.activeWorkspaceId === id ? removed.nextActiveId : s.activeWorkspaceId;
    const next = applyWorkspace(s, removed.workspaces, stay);
    persist(next, true);
    set(next);
    return next.activeWorkspaceId;
  },

  importProjectData: (incoming) => {
    const project: Project = {
      ...(normalizeProjectResources(normalizeExecution(incoming) as Project)),
      id: newId("p"),
      updatedAt: new Date().toISOString(),
    };
    set((s) => {
      const next: CatalogState = {
        ...s,
        projects: { ...s.projects, [project.id]: project },
        byProject: { ...s.byProject, [project.id]: { checkpoints: [], collapsed: [] } },
        activeProjectId: project.id,
      };
      persist(next, true);
      return next;
    });
    return project.id;
  },

  deleteProject: (id) => {
    set((s) => {
      const { [id]: _, ...projects } = s.projects;
      const { [id]: __, ...byProject } = s.byProject;
      const current = s.workspaces[s.activeWorkspaceId];
      const workspaces = current?.openPlanIds
        ? {
            ...s.workspaces,
            [s.activeWorkspaceId]: { ...current, openPlanIds: current.openPlanIds.filter((planId) => planId !== id) },
          }
        : s.workspaces;
      const next: CatalogState = {
        ...s,
        projects,
        byProject,
        workspaces,
        activeProjectId: s.activeProjectId === id ? null : s.activeProjectId,
      };
      persist(next, true);
      return next;
    });
  },

  saveProject: (project, chrome) => {
    set((s) => {
      const next: CatalogState = {
        ...s,
        projects: { ...s.projects, [project.id]: project },
        byProject: { ...s.byProject, [project.id]: chrome },
        activeProjectId: project.id,
      };
      persist(next);
      return next;
    });
  },

  mutateProject: (id, fn) => {
    const cur = get().projects[id];
    if (!cur) return false;
    const next = fn(structuredClone(cur));
    next.updatedAt = new Date().toISOString();
    set((s) => {
      const state: CatalogState = { ...s, projects: { ...s.projects, [id]: next } };
      persist(state, true);
      return state;
    });
    return true;
  },

  setActiveProjectId: (id) => {
    set((s) => {
      const next: CatalogState = { ...s, activeProjectId: id };
      persist(next);
      return next;
    });
  },

  setOpenPlanIds: (ids) => {
    const current = get().workspaces[get().activeWorkspaceId];
    if (!current) return;
    const prev = current.openPlanIds ?? [];
    if (prev.length === ids.length && prev.every((planId, index) => planId === ids[index])) return;
    set((s) => {
      const ws = s.workspaces[s.activeWorkspaceId];
      if (!ws) return s;
      const next: CatalogState = {
        ...s,
        workspaces: { ...s.workspaces, [s.activeWorkspaceId]: { ...ws, openPlanIds: ids } },
      };
      persist(next);
      return next;
    });
  },

  setTheme: (theme) => {
    set((s) => {
      const next: CatalogState = { ...s, theme };
      persist(next, true);
      return next;
    });
  },

  setProfile: (patch) => {
    set((s) => {
      const clip = (value: string | undefined) => (value ?? "").trim().slice(0, 40);
      const displayName = clip(patch.displayName ?? s.profile.displayName) || "나";
      const title = "title" in patch ? clip(patch.title) : s.profile.title;
      const affiliation = "affiliation" in patch ? clip(patch.affiliation) : s.profile.affiliation;
      const email = "email" in patch ? (patch.email ?? "").trim().slice(0, 80) : s.profile.email;
      const avatar = "avatar" in patch ? patch.avatar : s.profile.avatar;
      const next: CatalogState = {
        ...s,
        profile: {
          displayName,
          ...(title ? { title } : {}),
          ...(affiliation ? { affiliation } : {}),
          ...(email ? { email } : {}),
          ...(avatar ? { avatar } : {}),
        },
        // follow the rename when the filter pointed at "me"
        myTasksAssignee: s.myTasksAssignee === s.profile.displayName ? displayName : s.myTasksAssignee,
      };
      persist(next, true);
      return next;
    });
  },

  setMyTasksAssignee: (name) => {
    set((s) => {
      const next: CatalogState = { ...s, myTasksAssignee: name };
      persist(next);
      return next;
    });
  },

  setNavCollapsed: (collapsed) => {
    if (get().navCollapsed === collapsed) return;
    set((s) => {
      const next: CatalogState = { ...s, navCollapsed: collapsed };
      persist(next, true);
      return next;
    });
  },

  setUserWeeklyNote: (key, note) => {
    const nextNote = normalizeWeeklyNote(note);
    set((s) => {
      const userWeekly = { ...s.userWeekly };
      if (!nextNote.excludedIds.length && !nextNote.memo) delete userWeekly[key];
      else userWeekly[key] = nextNote;
      const next: CatalogState = { ...s, userWeekly };
      persist(next, true);
      return next;
    });
  },

  addResource: (resource) => {
    const id = newId("res");
    const newRes: UserResource = { ...resource, id };
    set((s) => {
      const next: CatalogState = {
        ...s,
        resources: { ...s.resources, [id]: newRes },
      };
      persist(next, true);
      return next;
    });
    return id;
  },

  updateResource: (id, patch) => {
    set((s) => {
      const cur = s.resources[id];
      if (!cur) return s;
      const next: CatalogState = {
        ...s,
        resources: { ...s.resources, [id]: { ...cur, ...patch } },
      };
      persist(next, true);
      return next;
    });
  },

  deleteResource: (id) => {
    set((s) => {
      const { [id]: _, ...resources } = s.resources;
      const next: CatalogState = { ...s, resources };
      persist(next, true);
      return next;
    });
  },

  addTeam: (team) => {
    const id = newId("team");
    const newTeam: OrganizationTeam = { ...team, id };
    set((s) => {
      const next: CatalogState = {
        ...s,
        teams: { ...s.teams, [id]: newTeam },
      };
      persist(next, true);
      return next;
    });
    return id;
  },

  updateTeam: (id, patch) => {
    set((s) => {
      const cur = s.teams[id];
      if (!cur) return s;
      const next: CatalogState = {
        ...s,
        teams: { ...s.teams, [id]: { ...cur, ...patch } },
      };
      persist(next, true);
      return next;
    });
  },

  deleteTeam: (id) => {
    set((s) => {
      const { [id]: _, ...teams } = s.teams;
      const next: CatalogState = { ...s, teams };
      persist(next, true);
      return next;
    });
  },

  applyOrgSnapshot: (teams, resources) => {
    set((s) => {
      const next: CatalogState = { ...s, teams, resources };
      persist(next, true);
      return next;
    });
  },

  getResourceWorkload: (resourceId) => {
    const { projects } = get();
    const assignments: ResourceWorkloadSummary["assignments"] = [];
    let totalAllocationRatio = 0;
    let totalPlannedMM = 0;

    for (const project of Object.values(projects)) {
      if (!Array.isArray(project.resourceAssignments)) continue;
      for (const asg of project.resourceAssignments) {
        if (asg.resourceId === resourceId) {
          assignments.push({
            projectId: project.id,
            projectName: project.name,
            projectRole: asg.projectRole,
            allocationRatio: asg.allocationRatio,
            plannedMM: asg.plannedMM,
            startDate: asg.startDate,
            endDate: asg.endDate,
          });
          totalAllocationRatio += asg.allocationRatio;
          totalPlannedMM += asg.plannedMM;
        }
      }
    }

    return {
      resourceId,
      totalAllocationRatio: Math.round(totalAllocationRatio * 100) / 100,
      totalPlannedMM: Math.round(totalPlannedMM * 10) / 10,
      projectCount: assignments.length,
      isOverAllocated: totalAllocationRatio > 1.05,
      assignments,
    };
  },

  getAllWorkloads: () => {
    const { resources } = get();
    const result: Record<string, ResourceWorkloadSummary> = {};
    for (const resId of Object.keys(resources)) {
      result[resId] = get().getResourceWorkload(resId);
    }
    return result;
  },
}));
